<!-- Wrapper for user-display that fetches user data -->
<template>
  <template v-if="loading > 0">
    <span v-if="displayName" :class="elementClass"> {{ displayName }}</span>
  </template>
  <user-display
    v-if="loading === 0"
    :user-id="resolvedUserId ?? ''"
    :display-name="resolvedDisplayName"
    :scale="scale"
    :archived-on="resolvedArchivedOn"
    :class="elementClass"
  />
  <span v-if="rotaAgency" class="small ms-1" :class="elementClass">
    <font-awesome-icon icon="id-card" />
    ({{ rotaAgency.name }})
  </span>
</template>

<script setup>
import { watch, ref, computed } from "vue";
import { useStore } from "vuex";
import { UserDisplay } from "@switchsystems/coactive-vue-lib";

const props = defineProps({
  userId: {
    type: String,
    default: null,
  },
  displayName: {
    type: String,
    default: null,
  },
  user: {
    type: Object,
    default: null,
  },
  agencyId: {
    type: String,
    default: null,
  },
  scale: {
    type: Number,
    default: 0.75,
  },
  class: {
    type: String,
    default: "",
  },
});

const store = useStore();
const loading = ref(0);
const rotaUser = ref(null);
const rotaAgency = ref(null);
const elementClass = props.class;

const fetchUser = async () => {
  if (!props.userId || props.userId === "nobody" || props.user) {
    return;
  }
  loading.value += 1;
  try {
    const response = await store.dispatch("rotausers/getRotaUser", {
      id: props.userId,
      options: { doNotCatchIfError: true },
    });
    rotaUser.value = response;
  } catch {
    console.warn("Failed to fetch user", props.userId);
  } finally {
    loading.value -= 1;
  }
};

const fetchAgency = async () => {
  if (!props.agencyId) {
    return;
  }
  if (props.agencyId === "nobody") {
    rotaAgency.value = { name: "Agency" };
    return;
  }
  try {
    loading.value += 1;
    const response = await store.dispatch("rotaagencies/getRotaAgency", {
      id: props.agencyId,
      options: { doNotCatchIfError: true },
    });
    rotaAgency.value = response;
  } catch {
    console.warn("Failed to fetch agency:", props.agencyId);
    rotaAgency.value = { name: "Agency" };
  } finally {
    loading.value -= 1;
  }
};

const resolvedUserId = computed(() => {
  return props.user?.id ?? props.user?.userId ?? props.userId;
});

const resolvedDisplayName = computed(() => {
  const displayName =
    rotaUser.value?.displayName ?? props.user?.displayName ?? props.displayName;

  if (!displayName) {
    return "Unknown User";
  }
  return displayName;
});

const resolvedArchivedOn = computed(() => {
  return rotaUser.value?.archivedOn ?? props.user?.archivedOn ?? undefined;
});

watch(() => props.userId, fetchUser, { immediate: true });
watch(() => props.agencyId, fetchAgency, { immediate: true });
</script>
